import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { supabase } from '../lib/supabase';
import { motion, AnimatePresence } from 'framer-motion';
import { createCheckoutSession } from '../lib/stripeService';
import { useAuth } from '../contexts/AuthContext';

const PurchaseCreditsModal = ({ isOpen, onClose, initialOption = 'credits', selectedCredits, selectedSubscription, initialBillingCycle = 'monthly' }) => {
  const { user, openAuthModal } = useAuth();
  const [creditPacks, setCreditPacks] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [selectedOption, setSelectedOption] = useState(initialOption);
  const [selectedCreditsValue, setSelectedCreditsValue] = useState(selectedCredits || null);
  const [selectedSubscriptionValue, setSelectedSubscriptionValue] = useState(selectedSubscription);
  const [billingCycle, setBillingCycle] = useState(initialBillingCycle);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isLoadingData, setIsLoadingData] = useState(true);

  useEffect(() => {
    const fetchCreditPacks = async () => {
      try {
        const { data, error } = await supabase
          .from('credit_packs')
          .select('*')
          .order('credits');
          
        if (error) throw error;
        
        setCreditPacks(data || []);
        
        // Set default selected credit value
        if (data && data.length > 0 && !selectedCreditsValue) {
          // Default to the middle credit pack, or the popular one
          const popularPack = data.find(pack => pack.popular === true);
          const middlePack = data[Math.min(1, data.length - 1)];
          setSelectedCreditsValue((popularPack || middlePack).credits);
        }
      } catch (error) {
        console.error('Error fetching credit packs:', error);
        setError('Failed to load credit packages. Please try again.');
      }
    };

    const fetchSubscriptions = async () => {
      try {
        const { data, error } = await supabase
          .from('subscriptions')
          .select('*')
          .order('price_monthly');
          
        if (error) throw error;
        
        setSubscriptions(data || []);
        
        if (data && data.length > 0) {
          // Default to middle tier (Pro) or whatever was passed in
          if (selectedSubscriptionValue) {
            setSelectedSubscriptionValue(selectedSubscriptionValue);
          } else {
            const proSubscription = data.find(sub => sub.tier_name === 'Pro');
            if (proSubscription) {
              setSelectedSubscriptionValue(proSubscription.id);
            } else {
              setSelectedSubscriptionValue(data[Math.min(1, data.length - 1)].id);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching subscriptions:', error);
        setError('Failed to load subscription information. Please try again.');
      } finally {
        setIsLoadingData(false);
      }
    };

    if (isOpen) {
      setSelectedOption(initialOption);
      setSelectedCreditsValue(selectedCredits);
      setSelectedSubscriptionValue(selectedSubscription);
      setBillingCycle(initialBillingCycle);
      setError(null);
      setIsLoadingData(true);
      
      fetchCreditPacks();
      fetchSubscriptions();
      
      // Prevent body scrolling when modal is open
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      // Restore body scrolling when modal is closed
      document.body.style.overflow = '';
    };
  }, [isOpen, initialOption, selectedCredits, selectedSubscription, initialBillingCycle]);

  const handlePurchase = async () => {
    // If user is not logged in, open auth modal
    if (!user) {
      onClose();
      openAuthModal('signup');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      let redirectUrl;
      
      if (selectedOption === 'credits') {
        const selectedPack = creditPacks.find(pack => pack.credits === selectedCreditsValue);
        if (!selectedPack) {
          throw new Error('Selected credit pack not found');
        }
        
        const { url } = await createCheckoutSession({
          priceId: selectedPack.stripe_price_id,
          type: 'credits',
          quantity: 1,
          successUrl: `${window.location.origin}/payment-success?type=credits&amount=${selectedPack.credits}`,
          cancelUrl: `${window.location.origin}/payment-cancelled`
        });
        
        redirectUrl = url;
      } else {
        const selectedSub = subscriptions.find(sub => sub.id === selectedSubscriptionValue);
        if (!selectedSub) {
          throw new Error('Selected subscription not found');
        }
        
        const priceId = billingCycle === 'monthly' 
          ? selectedSub.stripe_price_id_monthly 
          : selectedSub.stripe_price_id_yearly;
          
        if (!priceId) {
          throw new Error(`No Stripe price ID found for ${billingCycle} billing cycle`);
        }
        
        const { url } = await createCheckoutSession({
          priceId,
          type: 'subscription',
          quantity: 1,
          successUrl: `${window.location.origin}/payment-success?type=subscription&plan=${selectedSub.tier_name}&cycle=${billingCycle}`,
          cancelUrl: `${window.location.origin}/payment-cancelled`
        });
        
        redirectUrl = url;
      }
      
      // Redirect to Stripe checkout
      window.location.href = redirectUrl;
    } catch (error) {
      console.error('Purchase error:', error);
      setError(error.message || 'Failed to initiate payment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const selectedPack = creditPacks.find(pack => pack.credits === selectedCreditsValue);
  const packPrice = selectedPack ? selectedPack.price : 0;
  
  const getSubscriptionPrice = (subscription) => {
    if (!subscription) return 0;
    return billingCycle === 'monthly' 
      ? subscription.price_monthly 
      : subscription.price_yearly;
  };

  const selectedSubscriptionData = subscriptions.find(sub => sub.id === selectedSubscriptionValue);
  const subscriptionPrice = getSubscriptionPrice(selectedSubscriptionData);
  const getCostPerDocument = (subscription) => {
    if (billingCycle === 'monthly') {
      return (subscription.price_monthly / subscription.usage_cap).toFixed(2);
    } else {
      return (subscription.price_yearly / (subscription.usage_cap * 12)).toFixed(2);
    }
  };

  // Use createPortal to render the modal directly to the document body
  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center">
          {/* Backdrop */}
          <motion.div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div 
            className="bg-white rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden relative z-[10000] m-4"
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.2 }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-6">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-white">Purchase Credits</h2>
                <button 
                  onClick={onClose}
                  className="text-white/80 hover:text-white transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <p className="text-blue-100 mt-2">Choose how you want to add credits to your account</p>
            </div>

            {/* Body */}
            <div className="p-6">
              {/* Display error message if any */}
              {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {error}
                </div>
              )}
            
              {/* Toggle between credits and subscription */}
              <div className="flex justify-center mb-6">
                <div className="bg-gray-100 p-1 rounded-full inline-flex">
                  <button
                    className={`px-6 py-2 rounded-full text-sm font-medium transition-colors ${
                      selectedOption === 'credits' 
                        ? 'bg-primary text-white' 
                        : 'text-gray-600 hover:text-gray-800'
                    }`}
                    onClick={() => setSelectedOption('credits')}
                  >
                    Buy Credits
                  </button>
                  <button
                    className={`px-6 py-2 rounded-full text-sm font-medium transition-colors ${
                      selectedOption === 'subscription' 
                        ? 'bg-primary text-white' 
                        : 'text-gray-600 hover:text-gray-800'
                    }`}
                    onClick={() => setSelectedOption('subscription')}
                  >
                    Subscription
                  </button>
                </div>
              </div>

              {selectedOption === 'credits' ? (
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Select Credit Package</h3>
                  
                  {/* Credit packages */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    {creditPacks.map((pack) => (
                      <div 
                        key={pack.credits}
                        className={`border rounded-xl p-4 cursor-pointer transition-all ${
                          selectedCreditsValue === pack.credits
                            ? 'border-primary-500 ring-2 ring-primary/30 shadow-md'
                            : 'border-gray-200 hover:border-primary-300'
                        } ${pack.popular ? 'relative' : ''}`}
                        onClick={() => {
                          setSelectedCreditsValue(pack.credits);
                        }}
                      >
                        {pack.popular && (
                          <div className="absolute top-0 right-0 bg-blue-500 text-white text-xs font-bold py-1 px-2 rounded-bl-lg rounded-tr-lg">
                            POPULAR
                          </div>
                        )}
                        <div className="font-bold text-xl text-gray-800">{pack.credits} Credits</div>
                        <div className="text-2xl font-bold text-gray-900 mt-2">${pack.price.toFixed(2)}</div>
                        <div className="text-gray-500 text-sm mt-1">${(pack.price / pack.credits).toFixed(2)} per credit</div>
                      </div>
                    ))}
                  </div>

                  
                </div>
              ) : (
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Select a Subscription Plan</h3>
                  
                  {/* Billing Toggle */}
                  <div className="flex justify-center mb-6">
                    <div className="bg-gray-100 p-1 rounded-full inline-flex">
                      <button 
                        onClick={() => setBillingCycle('monthly')}
                        className={`px-6 py-2 rounded-full text-sm font-medium transition-colors ${
                          billingCycle === 'monthly' 
                            ? 'bg-primary text-white' 
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        Monthly
                      </button>
                      <button 
                        onClick={() => setBillingCycle('yearly')}
                        className={`px-6 py-2 rounded-full text-sm font-medium transition-colors ${
                          billingCycle === 'yearly' 
                            ? 'bg-primary text-white' 
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        Yearly <span className="text-xs font-bold text-green-500">Save 20%</span>
                      </button>
                    </div>
                  </div>
                  
                  {/* Subscription options */}
                  <div className="space-y-4 mb-6">
                    {subscriptions.map((sub) => (
                      <div 
                        key={sub.id}
                        className={`border rounded-xl p-4 cursor-pointer transition-all ${
                          selectedSubscriptionValue === sub.id
                            ? 'border-primary-500 ring-2 ring-primary/30 shadow-md'
                            : 'border-gray-200 hover:border-primary-300'
                        } ${sub.tier_name === 'Pro' ? 'relative' : ''}`}
                        onClick={() => setSelectedSubscriptionValue(sub.id)}
                      >
                        {sub.tier_name === 'Pro' && (
                          <div className="absolute top-0 right-0 bg-blue-500 text-white text-xs font-bold py-1 px-2 rounded-bl-lg rounded-tr-lg">
                            RECOMMENDED
                          </div>
                        )}
                        <div className="flex justify-between items-center">
                          <div>
                            <div className="font-bold text-xl text-gray-800">{sub.tier_name}</div>
                            <div className="text-gray-600">
                              {sub.usage_cap} credits {billingCycle === 'monthly' ? 'per month' : 'monthly'}
                            </div>
                            <div className="text-gray-500 text-sm mt-1">
                              ${getCostPerDocument(sub)} per document
                            </div>
                          </div>
                          <div className="text-2xl font-bold text-gray-900">
                            ${billingCycle === 'monthly' ? sub.price_monthly : sub.price_yearly}
                            <span className="text-sm font-normal text-gray-500">
                              /{billingCycle === 'monthly' ? 'mo' : 'yr'}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="bg-indigo-50 border border-indigo-100 p-4 rounded-lg mb-6">
                    <div className="flex items-start">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-indigo-500 mt-0.5 mr-2 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                      <span className="text-indigo-800 text-sm">
                        {billingCycle === 'monthly' 
                          ? 'Subscriptions automatically renew monthly. You can cancel anytime from your profile settings.' 
                          : 'Annual subscriptions offer a 20% discount and automatically renew yearly. You can cancel anytime from your profile settings.'}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Action buttons */}
              <div className="flex flex-col sm:flex-row gap-3 sm:justify-end mt-6">
                <button
                  onClick={onClose}
                  className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handlePurchase}
                  disabled={loading}
                  className="px-6 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg font-medium hover:shadow-lg hover:shadow-blue-500/30 transition-all disabled:opacity-70 flex justify-center items-center"
                >
                  {loading ? (
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  ) : (
                    selectedOption === 'credits' 
                      ? `Buy ${selectedCreditsValue} Credits for $${packPrice.toFixed(2)}`
                      : `Subscribe ${billingCycle === 'yearly' ? 'Yearly' : 'Monthly'} for $${subscriptionPrice}`
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default PurchaseCreditsModal;